import {
  Alert,
  Badge,
  Button,
  DataTable,
  Dialog,
  Field,
  Input,
  PageHeader,
  Select,
  Switch,
  toast,
  type DataColumn,
} from '@1k5-tech-es/grado-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { api, ApiError } from '../../api/client';
import type { Advisor, Team } from '../../api/types';
import { ErrorBlock, LoadingBlock } from '../../components/ui';

interface AdvisorForm {
  fullName: string;
  initials: string;
  email: string;
  teamId: string;
  startDate: string;
  isActive: boolean;
}

const emptyForm: AdvisorForm = { fullName: '', initials: '', email: '', teamId: '', startDate: '', isActive: true };

const COLUMNS: DataColumn<Advisor>[] = [
  {
    key: 'fullName',
    header: 'Nombre',
    sortable: true,
    render: (a) => (
      <span className="font-medium">
        {a.fullName}
        {a.initials && <span className="ml-2 text-xs text-muted-foreground">{a.initials}</span>}
      </span>
    ),
  },
  {
    key: 'teamCode',
    header: 'Equipo',
    sortable: true,
    render: (a) => (a.teamCode ? <Badge tone="brand">{a.teamCode}</Badge> : <Badge tone="warning">Sin equipo</Badge>),
  },
  { key: 'email', header: 'Email', render: (a) => a.email ?? '—' },
  {
    key: 'startDate',
    header: 'Incorporación',
    sortable: true,
    render: (a) => (a.startDate ? new Date(a.startDate).toLocaleDateString('es-ES') : '—'),
  },
  {
    key: 'isActive',
    header: 'Estado',
    render: (a) => (a.isActive ? <Badge tone="success">Activo</Badge> : <Badge>Inactivo</Badge>),
  },
];

export function AdvisorsPage() {
  const queryClient = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Advisor | 'new' | null>(null);
  const [search, setSearch] = useState('');

  const advisors = useQuery({
    queryKey: ['advisors', 'all'],
    queryFn: () => api.get<{ advisors: Advisor[] }>('/api/advisors?includeInactive=true').then((r) => r.advisors),
  });
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<{ teams: Team[] }>('/api/teams').then((r) => r.teams),
  });

  const visible = (advisors.data ?? []).filter(
    (a) => (showInactive || a.isActive) && a.fullName.toLowerCase().includes(search.toLowerCase()),
  );
  const withoutTeam = (advisors.data ?? []).filter((a) => a.isActive && !a.teamId).length;

  return (
    <>
      <PageHeader
        title="Asesores"
        subtitle="Alta, baja y equipo de cada asesor. El email permite que futuras integraciones identifiquen al asesor automáticamente."
        actions={
          <Button icon={Plus} onClick={() => setEditing('new')}>
            Nuevo asesor
          </Button>
        }
      />
      {withoutTeam > 0 && (
        <Alert tone="warning" title={`${withoutTeam} asesores activos no tienen equipo asignado`} className="mb-4">
          En su informe no aparecerá el equipo.
        </Alert>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <Input
          className="max-w-xs"
          aria-label="Buscar asesor"
          placeholder="Buscar…"
          prefix={<Search className="size-4" aria-hidden />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Switch label="Mostrar inactivos" checked={showInactive} onCheckedChange={setShowInactive} />
      </div>

      {advisors.isPending ? (
        <LoadingBlock />
      ) : advisors.isError ? (
        <ErrorBlock error={advisors.error} />
      ) : (
        <DataTable
          caption={`Asesores (${visible.length})`}
          rows={visible}
          columns={COLUMNS}
          rowKey={(a) => String(a.id)}
          pageSize={15}
          defaultSort={{ key: 'fullName', direction: 'asc' }}
          emptyLabel="No hay asesores que coincidan con la búsqueda."
          rowActions={(a) => (
            <Button variant="ghost" size="sm" icon={Pencil} onClick={() => setEditing(a)} aria-label={`Editar ${a.fullName}`}>
              Editar
            </Button>
          )}
        />
      )}

      {editing && (
        <AdvisorDialog
          advisor={editing === 'new' ? null : editing}
          teams={teams.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={(name) => {
            setEditing(null);
            toast.success(editing === 'new' ? `${name} dado de alta` : `${name} guardado`);
            void queryClient.invalidateQueries({ queryKey: ['advisors'] });
            void queryClient.invalidateQueries({ queryKey: ['teams'] });
            void queryClient.invalidateQueries({ queryKey: ['monthly'] });
          }}
        />
      )}
    </>
  );
}

function AdvisorDialog({
  advisor,
  teams,
  onClose,
  onSaved,
}: {
  advisor: Advisor | null;
  teams: Team[];
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const [form, setForm] = useState<AdvisorForm>(
    advisor
      ? {
          fullName: advisor.fullName,
          initials: advisor.initials ?? '',
          email: advisor.email ?? '',
          teamId: advisor.teamId ? String(advisor.teamId) : '',
          startDate: advisor.startDate ?? '',
          isActive: advisor.isActive,
        }
      : emptyForm,
  );
  const save = useMutation({
    mutationFn: () => {
      const body = {
        fullName: form.fullName,
        initials: form.initials || null,
        email: form.email || null,
        teamId: form.teamId ? Number(form.teamId) : null,
        startDate: form.startDate || null,
        isActive: form.isActive,
      };
      return advisor ? api.patch(`/api/advisors/${advisor.id}`, body) : api.post('/api/advisors', body);
    },
    onSuccess: () => onSaved(form.fullName),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }

  const set = <K extends keyof AdvisorForm>(key: K, value: AdvisorForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={advisor ? `Editar · ${advisor.fullName}` : 'Nuevo asesor'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button form="advisor-form" type="submit" loading={save.isPending}>
            Guardar
          </Button>
        </>
      }
    >
      <form id="advisor-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <Field label="Nombre completo" required className="sm:col-span-2">
          <Input minLength={2} value={form.fullName} onChange={(e) => set('fullName', e.target.value)} />
        </Field>
        <Field label="Iniciales">
          <Input maxLength={10} value={form.initials} onChange={(e) => set('initials', e.target.value)} />
        </Field>
        <Field label="Equipo">
          <Select value={form.teamId} onChange={(e) => set('teamId', e.target.value)}>
            <option value="">Sin equipo</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} · {t.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Email">
          <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
        </Field>
        <Field label="Fecha de incorporación">
          <Input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
        </Field>
        <div className="sm:col-span-2">
          <Switch
            label="Activo"
            description="Aparece en la tabla mensual y en los informes."
            checked={form.isActive}
            onCheckedChange={(checked) => set('isActive', checked)}
          />
        </div>
        {save.isError && (
          <Alert tone="danger" title="No se ha podido guardar" className="sm:col-span-2">
            {save.error instanceof ApiError ? save.error.message : 'Inténtalo de nuevo.'}
          </Alert>
        )}
      </form>
    </Dialog>
  );
}
